import { useEffect, useState } from "react";
import {
  Box,
  SpaceBetween,
  Header,
  ColumnLayout,
  Input,
  Checkbox,
  Select,
  FormField,
  Container,
  Tiles,
} from "@cloudscape-design/components-themed";
import { DivisionDict, addBlank } from "../default-constants";
import {
  BILLING_OPTIONS,
  CREDIT_PAYMENT_OPTIONS,
  D_BALANCE_PAYMENT_OPTIONS,
  D_CARD_PAYMENT_OPTIONS,
  DISABLED_VALUE,
  DISPLAY_OPTIONS,
  ENABLE_VALUE,
  ENABLED_DISABLED_OPTIONS,
  ENABLED_VALUE,
  LABELS,
  USAGE_TYPE_OPTIONS,
} from "./constants";
import {
  useGetMerchantStoreSiteIdQuery,
  useGetMerchantsSystemTabQuery,
} from "../../features/api";
import {
  MerchantsSystemTabSearchRequest,
  MerchantsSystemTabSearchResponse,
} from "../../features/api-interfaces";
import {
  getMerchantStoreSiteIdItems,
  getReconcilablePeriodLabel,
} from "./util";
import { formatDate } from "../../utils/format";

interface ISystemSettingTab {
  applicantCompanyCode: string;
}

export const SystemSettingTab = (props: ISystemSettingTab) => {
  const { applicantCompanyCode } = props;
  const [displayArea, setDisplayArea] = useState(DISPLAY_OPTIONS[0]);
  const [merchantStoreSiteIdList, setMerchantStoreSiteIdList] = useState<
    DivisionDict[]
  >([]);
  const [systemSettingInfo, setSystemSettingInfo] = useState(
    {} as MerchantsSystemTabSearchResponse
  );
  const [searchInput, setSearchInput] = useState({
    cpid: "",
    applicantCompanyCode,
  } as MerchantsSystemTabSearchRequest);
  const { data: merchantStoreSiteId } =
    useGetMerchantStoreSiteIdQuery(applicantCompanyCode);
  const { data: apiResponse } = useGetMerchantsSystemTabQuery(searchInput, {
    skip: false,
  });
  const showPaymentMethods = ["1", "2"].includes(displayArea.value);
  const showPaymentControl = ["1", "3"].includes(displayArea.value);
  const showCreditPattern = ["1", "4"].includes(displayArea.value);
  const showSystemItems = ["1", "5"].includes(displayArea.value);
  const showDPointAward = ["1", "6"].includes(displayArea.value);
  const { all, cashValue, prepaidValue } = ENABLE_VALUE;

  useEffect(() => {
    setMerchantStoreSiteIdList(
      addBlank(getMerchantStoreSiteIdItems(merchantStoreSiteId))
    );
  }, [merchantStoreSiteId]);

  useEffect(() => {
    if (apiResponse) {
      setSystemSettingInfo(apiResponse);
    }
  }, [apiResponse]);

  const changeEnabledValueCode = (
    checked: boolean,
    enabledValueCode: string
  ) => {
    let newEnabledValueCode = systemSettingInfo?.enabledValueCode ?? "";

    if (checked) {
      newEnabledValueCode = [cashValue.value, prepaidValue.value].includes(
        newEnabledValueCode
      )
        ? all.value
        : enabledValueCode;
    } else {
      if (enabledValueCode === cashValue.value) {
        newEnabledValueCode =
          newEnabledValueCode === cashValue.value ? "" : prepaidValue.value;
      } else {
        newEnabledValueCode =
          newEnabledValueCode === prepaidValue.value ? "" : cashValue.value;
      }
    }

    setSystemSettingInfo({
      ...systemSettingInfo,
      enabledValueCode: newEnabledValueCode,
    });
  };

  return (
    <SpaceBetween direction="vertical" size="m">
      <Container
        header={
          <ColumnLayout columns={2}>
            <Header variant="h2">{LABELS.MERCHANT_TITLE} </Header>
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                {apiResponse?.orderAdaptationDate && (
                  <h4>
                    {LABELS.BLANK +
                      LABELS.ADAPTED_DAY +
                      LABELS.BLANK +
                      formatDate(
                        apiResponse?.orderAdaptationDate ?? LABELS.BLANK
                      )}
                  </h4>
                )}
              </SpaceBetween>
            </Box>
          </ColumnLayout>
        }
      >
        <SpaceBetween direction="vertical" size="l">
          <ColumnLayout columns={4}>
            <FormField
              label={
                <SpaceBetween direction="horizontal" size="xs">
                  <Box variant="strong">{LABELS.SITE_ID}</Box>
                </SpaceBetween>
              }
              stretch={true}
            >
              <Select
                selectedOption={{ value: searchInput.cpid }}
                onChange={({ detail: { selectedOption } }) => {
                  setSearchInput({
                    ...searchInput,
                    cpid: selectedOption.value as string,
                  });
                }}
                options={merchantStoreSiteIdList}
                selectedAriaLabel="Selected"
                filteringType="auto"
              />
            </FormField>
          </ColumnLayout>
          <Container>
            <SpaceBetween direction="vertical" size="l">
              <ColumnLayout columns={4}>
                <FormField
                  label={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Box variant="strong">{LABELS.ITEM}</Box>
                    </SpaceBetween>
                  }
                  stretch={true}
                >
                  <Select
                    selectedOption={displayArea}
                    onChange={({ detail: { selectedOption } }) => {
                      setDisplayArea(selectedOption as DivisionDict);
                    }}
                    options={DISPLAY_OPTIONS}
                  />
                </FormField>
              </ColumnLayout>
              {showPaymentMethods && (
                <Container
                  header={<Header variant="h2">{LABELS.PAYMENT_TITLE}</Header>}
                >
                  <SpaceBetween direction="vertical" size="l">
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.CREDIT}</Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={ENABLED_DISABLED_OPTIONS}
                        value={systemSettingInfo?.creditCardPaymentFlag ?? ""}
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            creditCardPaymentFlag: value,
                            creditCardPaymentCreditPatternCode:
                              value === DISABLED_VALUE
                                ? ""
                                : systemSettingInfo?.creditCardPaymentCreditPatternCode ??
                                  "",
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.D_PAYMENT}</Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={ENABLED_DISABLED_OPTIONS}
                        value={systemSettingInfo?.dcmxPaymentFlag ?? ""}
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            dcmxPaymentFlag: value,
                            dcmxPaymentCreditPatternCode:
                              value === DISABLED_VALUE
                                ? ""
                                : systemSettingInfo?.dcmxPaymentCreditPatternCode ??
                                  "",
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.D_POINT}</Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={ENABLED_DISABLED_OPTIONS}
                        value={systemSettingInfo?.pointAppropriationFlag ?? ""}
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            pointAppropriationFlag: value,
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.PREALLOCATION}</Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={ENABLED_DISABLED_OPTIONS}
                        value={systemSettingInfo?.couponAppropriationFlag ?? ""}
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            couponAppropriationFlag: value,
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.INVOICE}</Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={ENABLED_DISABLED_OPTIONS}
                        value={systemSettingInfo?.billPaymentFlag ?? ""}
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            billPaymentFlag: value,
                            billPaymentCreditPatternCode:
                              value === DISABLED_VALUE
                                ? ""
                                : systemSettingInfo?.billPaymentCreditPatternCode ??
                                  "",
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.D_PAYMENT_BALANCE}</Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={ENABLED_DISABLED_OPTIONS}
                        value={systemSettingInfo?.bankAccountPaymentFlag ?? ""}
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            bankAccountPaymentFlag: value,
                            bankAccountPaymentCreditPatternCode:
                              value === DISABLED_VALUE
                                ? ""
                                : systemSettingInfo?.bankAccountPaymentCreditPatternCode ??
                                  "",
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.REFUSAL}</Box>
                        </SpaceBetween>
                      }
                    >
                      <SpaceBetween direction="horizontal" size="xs">
                        <Checkbox
                          onChange={({ detail: { checked } }) => {
                            setSystemSettingInfo({
                              ...systemSettingInfo,
                              refuseTargetBrandCodesVisa: checked
                                ? ENABLED_VALUE
                                : DISABLED_VALUE,
                            });
                          }}
                          checked={
                            systemSettingInfo?.refuseTargetBrandCodesVisa ===
                            ENABLED_VALUE
                          }
                        >
                          {LABELS.VISA}
                        </Checkbox>
                        <Checkbox
                          onChange={({ detail: { checked } }) => {
                            setSystemSettingInfo({
                              ...systemSettingInfo,
                              refuseTargetBrandCodesMaster: checked
                                ? ENABLED_VALUE
                                : DISABLED_VALUE,
                            });
                          }}
                          checked={
                            systemSettingInfo?.refuseTargetBrandCodesMaster ===
                            ENABLED_VALUE
                          }
                        >
                          {LABELS.MASTER}
                        </Checkbox>
                        <Checkbox
                          onChange={({ detail: { checked } }) => {
                            setSystemSettingInfo({
                              ...systemSettingInfo,
                              refuseTargetBrandCodesJcb: checked
                                ? ENABLED_VALUE
                                : DISABLED_VALUE,
                            });
                          }}
                          checked={
                            systemSettingInfo?.refuseTargetBrandCodesJcb ===
                            ENABLED_VALUE
                          }
                        >
                          {LABELS.JCB}
                        </Checkbox>
                        <Checkbox
                          onChange={({ detail: { checked } }) => {
                            setSystemSettingInfo({
                              ...systemSettingInfo,
                              refuseTargetBrandCodesAmex: checked
                                ? ENABLED_VALUE
                                : DISABLED_VALUE,
                            });
                          }}
                          checked={
                            systemSettingInfo?.refuseTargetBrandCodesAmex ===
                            ENABLED_VALUE
                          }
                        >
                          {LABELS.AMEX}
                        </Checkbox>
                        <Checkbox
                          onChange={({ detail: { checked } }) => {
                            setSystemSettingInfo({
                              ...systemSettingInfo,
                              refuseTargetBrandCodesDinersClub: checked
                                ? ENABLED_VALUE
                                : DISABLED_VALUE,
                            });
                          }}
                          checked={
                            systemSettingInfo?.refuseTargetBrandCodesDinersClub ===
                            ENABLED_VALUE
                          }
                        >
                          {LABELS.DINERS_CLUB}
                        </Checkbox>
                      </SpaceBetween>
                    </FormField>
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.USAGE}</Box>
                        </SpaceBetween>
                      }
                    >
                      <SpaceBetween direction="horizontal" size="xs">
                        <Checkbox
                          onChange={({ detail: { checked } }) =>
                            changeEnabledValueCode(checked, cashValue.value)
                          }
                          checked={[all.value, cashValue.value].includes(
                            systemSettingInfo?.enabledValueCode ?? ""
                          )}
                        >
                          {LABELS.CASH}
                        </Checkbox>
                        <Checkbox
                          onChange={({ detail: { checked } }) =>
                            changeEnabledValueCode(checked, prepaidValue.value)
                          }
                          checked={[all.value, prepaidValue.value].includes(
                            systemSettingInfo?.enabledValueCode ?? ""
                          )}
                        >
                          {LABELS.PREPAY}
                        </Checkbox>
                      </SpaceBetween>
                    </FormField>
                  </SpaceBetween>
                </Container>
              )}
              {showPaymentControl && (
                <Container
                  header={
                    <Header variant="h2">{LABELS.PAYMENT_CONTROL_TITLE}</Header>
                  }
                >
                  <SpaceBetween direction="vertical" size="l">
                    <ColumnLayout columns={4}>
                      <FormField
                        label={
                          <SpaceBetween direction="horizontal" size="xs">
                            <Box variant="strong">
                              {LABELS.SETTLEMENT_LIMIT_PRM}
                            </Box>
                          </SpaceBetween>
                        }
                        stretch={true}
                      >
                        <Input
                          value={systemSettingInfo?.newInfoPrice ?? ""}
                          onChange={({ detail: { value } }) =>
                            setSystemSettingInfo({
                              ...systemSettingInfo,
                              newInfoPrice: value,
                            })
                          }
                        />
                      </FormField>
                      <Box padding={{ top: "xxl" }} variant="awsui-key-label">
                        {LABELS.YEN_UNIT}
                      </Box>
                    </ColumnLayout>
                  </SpaceBetween>
                </Container>
              )}
              {showCreditPattern && (
                <Container
                  header={
                    <Header variant="h2">{LABELS.CREDIT_PATTERN_TITLE}</Header>
                  }
                >
                  <SpaceBetween direction="vertical" size="l">
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">
                            {LABELS.BILL_COMBINED_PAYMENT}
                          </Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={BILLING_OPTIONS}
                        value={
                          systemSettingInfo?.billPaymentCreditPatternCode ?? ""
                        }
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            billPaymentCreditPatternCode: value,
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">
                            {LABELS.PAY_WITH_D_PAYMENT}
                          </Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={D_BALANCE_PAYMENT_OPTIONS}
                        value={
                          systemSettingInfo?.bankAccountPaymentCreditPatternCode ??
                          ""
                        }
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            bankAccountPaymentCreditPatternCode: value,
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.D_CARD}</Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={D_CARD_PAYMENT_OPTIONS}
                        value={
                          systemSettingInfo?.dcmxPaymentCreditPatternCode ?? ""
                        }
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            dcmxPaymentCreditPatternCode: value,
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.CREDIT_PAYMENT}</Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={CREDIT_PAYMENT_OPTIONS}
                        value={
                          systemSettingInfo?.creditCardPaymentCreditPatternCode ??
                          ""
                        }
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            creditCardPaymentCreditPatternCode: value,
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                  </SpaceBetween>
                </Container>
              )}
              {showSystemItems && (
                <Container
                  header={<Header variant="h2">{LABELS.SYSTEM_TITLE}</Header>}
                >
                  <SpaceBetween direction="vertical" size="l">
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.USAGE_TYPE}</Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={USAGE_TYPE_OPTIONS}
                        value={systemSettingInfo?.cpidCoordinationStatus ?? ""}
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            cpidCoordinationStatus: value,
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">
                            {LABELS.NOT_SUBJECT_TO_PAYMENT}
                          </Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={ENABLED_DISABLED_OPTIONS}
                        value={systemSettingInfo?.settlementExcludeFlag ?? ""}
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            settlementExcludeFlag: value,
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                    <ColumnLayout columns={4}>
                      <FormField
                        label={
                          <SpaceBetween direction="horizontal" size="xs">
                            <Box variant="strong">{LABELS.MATCHING}</Box>
                          </SpaceBetween>
                        }
                        stretch={true}
                      >
                        <Input
                          value={getReconcilablePeriodLabel(
                            systemSettingInfo?.reconcilablePeriodCode ?? ""
                          )}
                          onChange={({ detail: { value } }) =>
                            setSystemSettingInfo({
                              ...systemSettingInfo,
                              reconcilablePeriodCode: value,
                            })
                          }
                        />
                      </FormField>
                    </ColumnLayout>
                  </SpaceBetween>
                </Container>
              )}
              {showDPointAward && (
                <Container
                  header={
                    <Header variant="h2">
                      {LABELS.D_POINT_PRESENTATION_TITLE}
                    </Header>
                  }
                >
                  <SpaceBetween direction="vertical" size="l">
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">
                            {LABELS.D_POINT_PRESENTATION}
                          </Box>
                        </SpaceBetween>
                      }
                    >
                      <Tiles
                        items={ENABLED_DISABLED_OPTIONS}
                        value={systemSettingInfo?.pointSetFlag ?? ""}
                        onChange={({ detail: { value } }) =>
                          setSystemSettingInfo({
                            ...systemSettingInfo,
                            pointSetFlag: value,
                          })
                        }
                        columns={3}
                      />
                    </FormField>
                    <ColumnLayout columns={4}>
                      <FormField
                        label={
                          <SpaceBetween direction="horizontal" size="xs">
                            <Box variant="strong">{LABELS.POINT_STANDARD}</Box>
                          </SpaceBetween>
                        }
                        stretch={true}
                      >
                        <Input
                          value={systemSettingInfo?.grantStandardAmount ?? ""}
                          onChange={({ detail: { value } }) =>
                            setSystemSettingInfo({
                              ...systemSettingInfo,
                              grantStandardAmount: value,
                            })
                          }
                        />
                      </FormField>
                      <Box padding={{ top: "xxl" }} variant="awsui-key-label">
                        {LABELS.YEN_UNIT}
                      </Box>
                    </ColumnLayout>
                    <ColumnLayout columns={4}>
                      <FormField
                        label={
                          <SpaceBetween direction="horizontal" size="xs">
                            <Box variant="strong">
                              {LABELS.POINT_PRESENTATION}
                            </Box>
                          </SpaceBetween>
                        }
                        stretch={true}
                      >
                        <Input
                          value={systemSettingInfo?.grantStandardPoint ?? ""}
                          onChange={({ detail: { value } }) =>
                            setSystemSettingInfo({
                              ...systemSettingInfo,
                              grantStandardPoint: value,
                            })
                          }
                        />
                      </FormField>
                      <Box padding={{ top: "xxl" }} variant="awsui-key-label">
                        {LABELS.POINT_UNIT}
                      </Box>
                    </ColumnLayout>
                  </SpaceBetween>
                </Container>
              )}
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};
